/**
 * The Learning contract (ADR 0019) — mandatory learning assignments, the second of the two new
 * clock-out-blocking conditions alongside the check-out report (ADR 0009).
 *
 * REUSES EXISTING PERMISSIONS. `learning:read`, `learning:manage`, `learning.assign:execute` were
 * already in the permission catalogue (`packages/shared/src/permissions/catalogue.ts`), assigned to
 * EMPLOYEE / HR_ADMIN / MANAGER respectively, with no model or route behind them until this contract.
 * No new permission is introduced for learning.
 *
 * MINIMAL BY DESIGN. This is not a course-authoring or LMS module — it is exactly enough to assign a
 * mandatory item to a person, let them mark it complete, and let the clock-out gate ask "does this
 * person have anything mandatory still open." `title`/`description` are free text; there is no
 * content player, no quiz, no versioning. Extend later if the product needs more.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { LEARNING_ASSIGNMENT_STATUSES } from '../enums.js';

const c = initContract();

export const learningAssignmentStatusSchema = z.enum(LEARNING_ASSIGNMENT_STATUSES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

const learningAssignmentSchemaShape = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  personId: uuidSchema,
  title: z.string(),
  description: z.string().nullable(),
  dueDate: dateOnlySchema.nullable(),
  isMandatory: z.boolean(),
  status: learningAssignmentStatusSchema,
  completedAt: instantSchema.nullable(),
  assignedByPersonId: uuidSchema,
  createdAt: instantSchema,
  updatedAt: instantSchema,
  /** Set when HR turned this item into a course (training redesign §9.2) — status `converted`. */
  convertedToAssignmentId: uuidSchema.nullable(),
});
export type LearningAssignment = z.infer<typeof learningAssignmentSchemaShape>;
export const learningAssignmentSchema: z.ZodType<LearningAssignment> = learningAssignmentSchemaShape;

// ---------------------------------------------------------------------------
// Request/response schemas
// ---------------------------------------------------------------------------

export const createLearningAssignmentBodySchema = z.object({
  personId: uuidSchema,
  title: z.string().trim().min(1).max(200),
  description: z.string().trim().max(2000).optional(),
  dueDate: dateOnlySchema.optional(),
  isMandatory: z.boolean().default(true),
});

export const listLearningAssignmentsQuerySchema = paginationQuerySchema.extend({
  personId: uuidSchema.optional(),
  status: learningAssignmentStatusSchema.optional(),
});

export const learningAssignmentParamsSchema = z.object({ id: uuidSchema });

export const waiveLearningAssignmentBodySchema = z.object({
  reason: z.string().trim().min(1).max(500),
});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const learningContract = c.router(
  {
    list: {
      method: 'GET',
      path: '',
      query: listLearningAssignmentsQuerySchema,
      responses: { 200: paginatedSchema(learningAssignmentSchema), 400: errorBodySchema },
      // Signed-in only, not a permission list: every employee may read their OWN assignments with
      // no permission at all (same shape as an "assigned approver, self-service read" list route —
      // see route-permissions.test.ts). Reading someone ELSE's is what the handler gates, on
      // learning:manage or learning.assign:execute, checked conditionally inside it.
      metadata: routeMeta(null),
      summary:
        "Older learning items (pre-LMS to-dos, not courses): the caller's own, or with "
        + "permission another person's. Course assignments are trainingAssignments",
    },
    create: {
      method: 'POST',
      path: '',
      body: createLearningAssignmentBodySchema,
      responses: { 201: learningAssignmentSchema, 400: errorBodySchema, 403: errorBodySchema },
      metadata: routeMeta(['learning.assign:execute', 'learning:manage']),
      summary:
        'Assign an older-style learning item (a titled to-do, not a course) to one person. To '
        + 'assign an LMS course use trainingAssignments__assign',
    },
    complete: {
      method: 'POST',
      path: '/:id/complete',
      pathParams: learningAssignmentParamsSchema,
      body: z.object({}),
      responses: { 200: learningAssignmentSchema, 400: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      // Signed-in only: an employee completes their own assignment. The handler checks the caller
      // is the assignment's own person — there is no meaningful org-wide "complete" permission.
      metadata: routeMeta(null),
      summary: "Mark the caller's own older learning item (not a course) complete",
    },
    waive: {
      method: 'POST',
      path: '/:id/waive',
      pathParams: learningAssignmentParamsSchema,
      body: waiveLearningAssignmentBodySchema,
      responses: { 200: learningAssignmentSchema, 400: errorBodySchema, 403: errorBodySchema, 404: errorBodySchema },
      metadata: routeMeta('learning:manage'),
      summary:
        'HR excuses an older mandatory learning item (not a course) without the employee '
        + 'completing it',
    },
  },
  { pathPrefix: '/learning-assignments' },
);
